import type { CmtsHttpClient } from "../api/cmtsHttpClient.js";
import { CMTS_API } from "../api/cmtsApiPaths.js";
import { mapCollection } from "./repositoryCollection.js";
import { repositoryCall, type RepositoryResult } from "./repositoryResult.js";
import { requireCmtsGuid } from "../utils/cmtsGuid.js";


export type MessageRepository = ReturnType<typeof createMessageRepository>;

export function createMessageRepository(client: CmtsHttpClient) {
  const query = (tenantId?: string) => ({ query: { tenantId } });
  return {
    forComplaint: (complaintId: string, tenantId?: string): Promise<RepositoryResult<unknown>> => repositoryCall(() => {
      const backendComplaintId = requireCmtsGuid(complaintId, "Messages complaintId");
      return client.get<unknown>(CMTS_API.complaints.messages(backendComplaintId), query(tenantId));
    }, "CMTS_MESSAGES_COMPLAINT"),
    sendToComplaint: (complaintId: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<unknown>> => repositoryCall(() => {
      const backendComplaintId = requireCmtsGuid(complaintId, "Message send complaintId");
      return client.post<unknown>(CMTS_API.complaints.messages(backendComplaintId), payload, query(tenantId));
    }, "CMTS_MESSAGE_SEND_COMPLAINT"),
    threads: (tenantId?: string) => repositoryCall(async () => mapCollection(await client.get<unknown>(CMTS_API.messages.threads, query(tenantId)), (x) => x as Record<string, unknown>), "CMTS_MESSAGE_THREADS"),
    createThread: (
  payload: Record<string, unknown>,
) =>
  repositoryCall(
    () => {
      const recipient =
        typeof payload.recipientUserId === "string"
          ? payload.recipientUserId.trim()
          : "";

      if (!recipient) {
        throw new Error(
          "Please select a recipient.",
        );
      }

      const recipientUserId =
        requireCmtsGuid(
          recipient,
          "Message recipient user id",
        );

      const complaintId =
        typeof payload.complaintId === "string" &&
        payload.complaintId.trim()
          ? requireCmtsGuid(
              payload.complaintId,
              "Message complaint id",
            )
          : undefined;

      const messageBody =
        typeof payload.messageBody === "string"
          ? payload.messageBody.trim()
          : typeof payload.body === "string"
            ? payload.body.trim()
            : "";

      if (!messageBody) {
        throw new Error(
          "Message body is required.",
        );
      }

      return client.post<unknown>(
        CMTS_API.messages.threads,
        {
          tenantId: payload.tenantId,
          subject: payload.subject,
          threadType: payload.threadTypeCode ?? payload.threadType ?? "GENERAL",
          confidentiality: payload.visibilityCode ?? payload.visibility ?? payload.confidentiality ?? "INTERNAL",
          linkedEntityType: complaintId ? "Complaint" : payload.linkedEntityType,
          linkedEntityId: complaintId ?? payload.linkedEntityId,
          parentComplaintId: complaintId ?? payload.parentComplaintId,
          participantJson: JSON.stringify([recipientUserId]),
          initialMessage: messageBody,
        },
      );
    },
    "CMTS_MESSAGE_THREAD_CREATE",
  ),
   addToThread: (
  threadId: string,
  payload: Record<string, unknown>,
  tenantId?: string,
) =>
  repositoryCall(
    () => {
      const backendThreadId =
        requireCmtsGuid(
          threadId,
          "Message add thread id",
        );

      const messageBody =
        typeof payload.messageBody === "string"
          ? payload.messageBody.trim()
          : typeof payload.body === "string"
            ? payload.body.trim()
            : "";

      if (!messageBody) {
        throw new Error(
          "Reply message is required.",
        );
      }

      return client.post<unknown>(
        CMTS_API.messages.addToThread(
          backendThreadId,
        ),
        {
          tenantId,
          body: messageBody,
          bodyFormat: payload.bodyFormat ?? "TEXT",
          messageDirection: payload.messageDirection ?? "INTERNAL",
          confidentiality: payload.confidentiality ?? payload.visibility ?? "INTERNAL",
          hasEvidenceAttachment: Array.isArray(payload.evidenceDocumentIds) && payload.evidenceDocumentIds.length > 0,
        },
        query(tenantId),
      );
    },
    "CMTS_MESSAGE_THREAD_ADD",
  ),
    markRead: (id: string, tenantId?: string) => repositoryCall(() => client.post<unknown>(CMTS_API.messages.markRead(requireCmtsGuid(id, "Message thread mark read id")), { tenantId }), "CMTS_MESSAGE_READ"),
    markUnread: (id: string, tenantId?: string) => repositoryCall(() => client.post<unknown>(CMTS_API.messages.markUnread(requireCmtsGuid(id, "Message thread mark unread id")), { tenantId }), "CMTS_MESSAGE_UNREAD"),
    archive: (id: string, payloadOrTenant?: Record<string, unknown> | string, tenantId?: string) => repositoryCall(() => {
      const effectiveTenant = typeof payloadOrTenant === "string" ? payloadOrTenant : tenantId;
      return client.post<unknown>(CMTS_API.messages.archive(requireCmtsGuid(id, "Message thread archive id")), { ...(typeof payloadOrTenant === "object" ? payloadOrTenant : {}), tenantId: effectiveTenant }, query(effectiveTenant));
    }, "CMTS_MESSAGE_ARCHIVE"),
    createTask: (id: string, payloadOrTenant?: Record<string, unknown> | string, tenantId?: string) => repositoryCall(() => {
      const effectiveTenant = typeof payloadOrTenant === "string" ? payloadOrTenant : tenantId;
      return client.post<unknown>(CMTS_API.messages.createTask(requireCmtsGuid(id, "Message thread create task id")), { ...(typeof payloadOrTenant === "object" ? payloadOrTenant : {}), tenantId: effectiveTenant }, query(effectiveTenant));
    }, "CMTS_MESSAGE_CREATE_TASK"),
    thread: (threadId: string, tenantId?: string) => repositoryCall(async () => {
      const backendThreadId = requireCmtsGuid(threadId, "Message thread detail id");
      return client.get<unknown>(CMTS_API.messages.thread(backendThreadId), query(tenantId));
    }, "CMTS_MESSAGE_THREAD_DETAIL"),
    delete: (
  id: string,
  tenantId?: string,
) =>
  repositoryCall(
    () => {
      const messageId =
        requireCmtsGuid(
          id,
          "Message delete id",
        );

      return client.delete<unknown>(
        CMTS_API.messages.delete(
          messageId,
        ),
        {
          query: {
            tenantId,
          },
        },
      );
    },
    "CMTS_MESSAGE_DELETE",
  ),
  };
}
